<script setup lang="ts">
  import { defineProps } from 'vue'

  const props = defineProps<{
    websitePrice: number,
    designPrice: number,
    videoPrice: number,
    totalPrice: number,
    websiteMaintenance: number,
    designSocialMediaService: { price: number, frequency: string | null },
    videoServices: {
      videoMonthlyServicePrice: number,
      videoWeeklyServicePrice: number,
    },
    yearlyTotal: number,
    monthlyTotal: number,
    weeklyTotal: number,
  }>()

  const illustration = '/assets/graphic.png'
  const refreshPage = (): void => {
    window.location.reload()
  }
</script>

<template>
  <div class="bg-secondary-1 w-full md:w-80 border-t md:border-t-0 md:border-l border-gray-200 flex flex-col">
    <div class="p-6 relative z-10">
      <!-- Summary Heading -->
      <h2 class="text-lg font-bold mb-6 text-secondary-3">
        Summary
      </h2>
      <div class="space-y-4">
        <!-- Website & Apps Plan -->
        <div>
          <div class="flex justify-between items-center text-sm text-secondary-3">
            <span>Website & Apps Plan</span>
            <span class="font-bold">{{ props.websitePrice.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.websiteMaintenance > 0"
            class="flex justify-between items-center text-xs mt-1 pl-4">
            <span class="text-secondary-2 text-xs">+ Yearly Maintenance</span>
            <span class="font-bold text-secondary-2">{{ props.websiteMaintenance.toLocaleString() }}</span>
          </div>
        </div>
        <!-- Design Plan -->
        <div>
          <div class="flex justify-between items-center text-sm text-secondary-3">
            <span>Design Plan</span>
            <span class="font-bold">{{ props.designPrice.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.designSocialMediaService.price > 0"
            class="flex justify-between items-center text-xs mt-1 pl-4">
            <span class="text-secondary-2 text-xs">
              + {{ props.designSocialMediaService.frequency === 'monthly' ? 'Monthly' : 'Weekly' }} Service
            </span>
            <span class="font-bold text-secondary-2">{{ props.designSocialMediaService.price.toLocaleString() }}</span>
          </div>
        </div>
        <!-- Video Plan -->
        <div>
          <div class="flex justify-between items-center text-sm">
            <span class="text-secondary-3">Video Plan</span>
            <span
              v-if="!props.videoServices || (props.videoServices.videoMonthlyServicePrice === 0 && props.videoServices.videoWeeklyServicePrice === 0)"
              class="font-bold text-secondary-2">{{ props.videoPrice.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.videoServices.videoMonthlyServicePrice > 0"
            class="flex justify-between items-center text-xs mt-1 pl-4">
            <span class="text-secondary-2 text-xs">+ Monthly Service{{ props.videoServices.videoMonthlyServicePrice > 0 && props.videoServices.videoWeeklyServicePrice > 0 ? 's' : '' }}</span>
            <span class="font-bold text-secondary-2">{{ props.videoServices.videoMonthlyServicePrice.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.videoServices.videoWeeklyServicePrice > 0"
            class="flex justify-between items-center text-xs mt-1 pl-4">
            <span class="text-secondary-2 text-xs">+ Weekly Service{{ props.videoServices.videoMonthlyServicePrice > 0 && props.videoServices.videoWeeklyServicePrice > 0 ? 's' : '' }}</span>
            <span class="font-bold text-secondary-2">{{ props.videoServices.videoWeeklyServicePrice.toLocaleString() }}</span>
          </div>
        </div>
        <!-- Total Price -->
        <div class="border-t border-secondary-2/50 pt-4 mt-4">
          <div class="flex justify-between items-center font-bold text-sm">
            <span class="text-secondary-3">Estimated Budget:</span>
            <span class="text-lg text-primary-700">{{ props.totalPrice.toLocaleString() }}</span>
          </div>
          <!-- Yearly, Monthly, and Weekly Totals -->
          <div
            v-if="props.yearlyTotal > 0"
            class="flex justify-between items-center text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Yearly Total</span>
            <span class="font-bold text-secondary-2">{{ props.yearlyTotal.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.monthlyTotal > 0"
            class="flex justify-between items-center text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Monthly Total</span>
            <span class="font-bold text-secondary-2">{{ props.monthlyTotal.toLocaleString() }}</span>
          </div>
          <div
            v-if="props.weeklyTotal > 0"
            class="flex justify-between items-center text-sm mt-2">
            <span class="text-secondary-2 text-xs">+ Weekly Total</span>
            <span class="font-bold text-secondary-2">{{ props.weeklyTotal.toLocaleString() }}</span>
          </div>
          <p class="text-xs text-secondary-2 mt-3">
            *all prices are in IDR
          </p>
        </div>
        <!-- Buttons -->
        <div class="grid grid-cols-2 gap-3 mt-8">
          <button
            type="button"
            class="text-primary-700 font-medium hover:underline text-sm"
            @click="refreshPage">
            Reset
          </button>
          <button
            type="button"
            class="bg-primary-700 text-white py-2 px-3 rounded-lg font-medium transition hover:bg-primary-600 text-sm">
            Consult Now
          </button>
        </div>
      </div>
    </div>

    <!-- Illustration -->
    <div class="flex-1 flex w-full items-end justify-end pointer-events-none opacity-50 md:opacity-100 size-64 min-h-10 pl-12">
      <img
        :src="illustration"
        alt="Illustration"
        class="h-full min-h-24 object-contain max-h-max">
    </div>
  </div>
</template>
